import { Injectable } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';

import { AppConstants } from '@models/appConstants';
import { RandomStringGeneratorProvider } from '@services/helpers/random-string-generator';
import { UtilsServiceProvider } from '@services/utils/utils-service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { ValidationProvider } from '@services/helpers/validation.service';
import { EmailAutocomplete, EmailSendData, JobClient } from '@models/models';

interface AttachmentsData {
	attachments: Array<any>;
	filesSize: number;
}

interface Attachments {
	storedFiles: Array<any>;
	uploadedFiles: Array<any>;
}

@Injectable()
export class EmailServiceProvider {
	private emptyAttachmentsData: AttachmentsData = {
		attachments: [],
		filesSize: 0
	};

	emailFn: Function = null;

	clientToCheckSubscription: BehaviorSubject<any> = new BehaviorSubject(null);

	constructor(
		private messageService: MessageService,
		private validationService: ValidationProvider,
		private randomStringGenerator: RandomStringGeneratorProvider,
		private utilsService: UtilsServiceProvider,
		public datePipe: DatePipe,
		private sanitizer: DomSanitizer
	) {}

	get clientToCheckSubscription$(): Observable<any> {
		return this.clientToCheckSubscription.asObservable();
	}

	emitClientToCheckSubscription(client: any): void {
		this.clientToCheckSubscription.next(client);
	}

	setMessageData(
		data,
		client: JobClient,
		secondaryClient: JobClient
	): EmailSendData {
		let { emailSubject, emailText, mailSignature } = data;
		if (mailSignature) {
			emailText += `<br><br>${mailSignature}`;
		}
		const emailTo = client.email ? [client.email] : [];
		if (secondaryClient) {
			emailTo.push(secondaryClient.email);
		}

		return {
			emailSubject: emailSubject || '',
			emailText: emailText || '',
			emailTo,
			ccEmails: [],
			attachmentsData: JSON.parse(JSON.stringify(this.emptyAttachmentsData))
		};
	}

	getClientEmail(client): string {
		const stringEmail = Object.assign(client.name);
		return stringEmail.substring(
			stringEmail.indexOf('(') + 1,
			stringEmail.length - 1
		);
	}

	prepareAttachments(attachmentsData): Attachments {
		return {
			storedFiles: attachmentsData.attachments.filter(file => file.id),
			uploadedFiles: attachmentsData.attachments.filter(file => !file.id)
		};
	}

	isAttachmentsValid(attachmentsData): boolean {
		const maxSize = 10; // 10Mb
		if (attachmentsData.filesSize > maxSize) {
			this.messageService.showToaster('w_0', 'attachments');
			return false;
		}
		return true;
	}

	prepareSignatureToShow(value: string): string {
		const imgTag = '<img id="signatureImg';
		if (!value.includes(imgTag)) {
			return value;
		}
		const signatureLink = 'SN/images/signature/';
		const updatedLink = `${AppConstants.HOST}/${signatureLink}`;
		if (value.includes(updatedLink)) {
			return value;
		}
		return value.replace(signatureLink, updatedLink);
	}

	resetSignatureLink(value: string) {
		const imgTag = '<img id="signatureImg';
		if (!value.includes(imgTag)) {
			return value;
		}

		const imgIndex = value.indexOf(imgTag);
		const closeIndex = value.indexOf('>', imgIndex);
		const addedServerString = /^(https?:\/\/).+\/$/;

		const fullImgTag = value.substring(imgIndex, closeIndex + 1).trim();
		const newImgTag = fullImgTag.replace(addedServerString, '');

		return value.replace(fullImgTag, newImgTag);
	}

	addMaiStyling(emailText: string): string {
		const emptyParagraph = '<p></p>';

		if (!emailText) {
			return emailText || '';
		}

		if (emailText === emptyParagraph) {
			return '';
		}

		const tmpId = `emailText_styling_${this.randomStringGenerator.getMixedString(
			9
		)}`;

		const tempTextEl = document.createElement('div');
		tempTextEl.setAttribute('id', tmpId);
		tempTextEl.className = 'hide';
		document.body.appendChild(tempTextEl);

		tempTextEl.innerHTML = emailText;

		this.mailStyling(tmpId);

		const message = tempTextEl.innerHTML;
		tempTextEl.remove();

		return message;
	}

	private mailStyling = (id: string): void => {
		const styleObj = {
			P: 'margin:0 0 2px;',
			LI: 'list-style-position: inside;',
			SPAN: 'padding:0;',
			DIV: 'margin:0;',
			BLOCKQUOTE:
				'margin: 0 0 0 0.8ex; border-left: 1px #ccc solid; padding-left: 1ex;'
		};

		const allTags = document.getElementById(id).querySelectorAll('*');

		let i = 0;
		const max = allTags.length;

		for (; i < max; i += 1) {
			const thisTag = allTags[i];
			const tagInlineStyle = thisTag.getAttribute('style');
			const elementStyle = window.getComputedStyle(thisTag);

			let tagStyle = '';
			const textAlign = elementStyle.getPropertyValue('text-align');
			const color = elementStyle.getPropertyValue('color');
			const fontSize = elementStyle.getPropertyValue('font-size');
			const fontFamily = elementStyle.getPropertyValue('font-family');
			if (
				textAlign &&
				tagInlineStyle &&
				tagInlineStyle.includes('text-align')
			) {
				tagStyle += `text-align:${textAlign};`;
			}
			if (color && tagInlineStyle && tagInlineStyle.includes('color')) {
				tagStyle += `color:${color};`;
			}
			if (fontSize && tagInlineStyle && tagInlineStyle.includes('font-size')) {
				tagStyle += `font-size:${fontSize}!important;font-size:${fontSize};`;
			} else {
				tagStyle += 'font-size:14px!important;font-size:14px;';
			}
			if (
				fontFamily &&
				tagInlineStyle &&
				tagInlineStyle.includes('font-family')
			) {
				tagStyle += `font-family:${fontFamily};`;
			}

			const thatTag = thisTag.tagName;
			tagStyle += styleObj[thatTag] || '';

			thisTag.setAttribute('style', tagStyle);
		}
	};

	filterEmailAutocomplete(
		inputData: Array<EmailAutocomplete>,
		filterData
	): Array<EmailAutocomplete> {
		if (!filterData.length) {
			return inputData;
		}

		const filterParsedData = filterData.map((data): string =>
			(data.email || data.label || data).trim()
		);

		return inputData.filter(
			(email: EmailAutocomplete): boolean =>
				!filterParsedData.includes(email.email)
		);
	}

	addSubjectVariable(element: HTMLInputElement, variable: string): string {
		const stringStart = `${element.value.slice(0, element.selectionStart)}`;
		const stringEnd = `${element.value.slice(
			element.selectionEnd || element.selectionStart
		)}`;
		const cursorPos = (stringStart + variable).length + 1;

		element.value = `${stringStart}${variable} ${stringEnd}`;

		element.focus();
		element.setSelectionRange(cursorPos, cursorPos);
		return element.value;
	}

	prepareEmails(emailData: EmailSendData): Partial<EmailSendData> {
		const resData: Partial<EmailSendData> = {};
		if (emailData.emailTo[0]) {
			[resData.emailTo] = this.getEmailFromMailString(
				emailData.emailTo as Array<string>
			);
		}

		if (emailData.emailTo.length > 1) {
			resData.secondaryToEmails = this.getEmailFromMailString(
				emailData.emailTo.slice(1) as Array<string>
			);
		}

		const emailCc = emailData.emailCc || emailData.ccEmails;
		if (emailCc && emailCc.length) {
			resData.ccEmails = this.getEmailFromMailString(emailCc);
		}

		resData.emailSubject = emailData.emailSubject
			? emailData.emailSubject.trim()
			: '';

		return resData;
	}

	validateMails(mailData: Partial<EmailSendData>): boolean {
		const { emailTo, secondaryToEmails, ccEmails, emailSubject } = mailData;
		const allEmails = [emailTo];

		if (!emailTo) {
			this.messageService.showToaster('e_3', 'email');
			return false;
		}

		if (!this.validationService.isEmailValid(emailTo)) {
			this.messageService.showToaster('e_6', 'email', emailTo as string);
			return false;
		}

		if (secondaryToEmails) {
			const [invalidEmail] = secondaryToEmails.filter(
				(email: string): boolean => !this.validationService.isEmailValid(email)
			);

			allEmails.push(...secondaryToEmails);

			if (invalidEmail) {
				this.messageService.showToaster('e_6', 'email', invalidEmail);
				return false;
			}
		}

		if (ccEmails) {
			const [invalidEmail] = ccEmails.filter(
				(email: string): boolean => !this.validationService.isEmailValid(email)
			);
			allEmails.push(...ccEmails);

			if (invalidEmail) {
				this.messageService.showToaster('e_6', 'email', invalidEmail);
				return false;
			}
		}

		const [duplicate] = this.findEmailDuplicates(allEmails as Array<string>);
		if (duplicate) {
			this.messageService.showToaster('e_4', 'email', duplicate);
			return false;
		}

		if (!this.isSubjectValid(emailSubject)) {
			return false;
		}

		return true;
	}

	isSubjectValid(subject: string): boolean {
		if (!subject) {
			this.messageService.showToaster('e_2', 'email');
			return false;
		}

		if (subject && subject.length > 255) {
			this.messageService.showToaster('e_5', 'email');
			return false;
		}
		return true;
	}

	private findEmailDuplicates(emails: Array<string>): Array<string> {
		const cache = {};
		const result = [];

		emails.forEach((item: string): void => {
			if (!cache[item]) {
				cache[item] = 0;
			}
			cache[item] += 1;
		});

		for (const email in cache) {
			if (cache[email] >= 2) {
				result.push(email);
			}
		}

		return result;
	}

	getEmailFromMailString(emails): Array<string> {
		return emails
			.map((item): string => item.email || item.label || item)
			.map((email: string): string => email.trim());
	}

	getEmailTextReply(emailText: string, text: string): string {
		return `<br><br>
                <div class="gmail_quote color-grey">${emailText}
                    <blockquote class="gmail_quote color-grey">${text}</blockquote>
                </div>`;
	}

	escapeEmailDetailsHtml(str: string): string {
		return str
			.replace(/&/g, '&amp;')
			.replace(/>/g, '&gt;')
			.replace(/</g, '&lt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
			.replace(/`/g, '&#96;');
	}

	escapeEmailTextHtml(text: string): string {
		let matchedText: any = text.replace(/[\r\n]+/g, ' ');
		matchedText = /<body.*>(.*?)<\/body>/gim.exec(matchedText);
		return matchedText ? matchedText[0] : text;
	}

	trimStyleTagsHtml(text: string): string {
		return text.replace(/<style.*?<\/style>/g, '');
	}

	escapeEmailBodyDetailsHtml(str: string): string {
		const emailWithTagRegex = /<\/?([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)>/gi;
		return str.replace(emailWithTagRegex, (matched): string =>
			matched.replace(/>/g, '&gt;').replace(/</g, '&lt;')
		);
	}

	getReplyText(mailData): string {
		return `<br/><br/>
                 ${mailData.emailTextReply}
                 <br/><br/>
                 ${mailData.mailSignature || ''}
                `;
	}

	getReplyDateInFormat(date: string): string {
		const dateFormatForReply = "EEE, MMM dd, yyyy 'at' HH:mm a";
		return this.datePipe.transform(date, dateFormatForReply);
	}

	getFormattedReplyData(data) {
		const { subject, to, cc, date, allFromName, text } = data;
		const formattedDate = this.getReplyDateInFormat(date);
		const mailData = JSON.parse(JSON.stringify(data));

		mailData.emailSubject = subject;
		mailData.emailTo = to;
		mailData.emailCc = cc;
		mailData.reply = true;
		mailData.validMsg = true;

		try {
			!!this.sanitizer.bypassSecurityTrustHtml(text);

			const emailDetails = this.escapeEmailDetailsHtml(
				`On ${formattedDate} ${allFromName} wrote:`
			);
			const matchedText = this.escapeEmailTextHtml(text);
			mailData.emailTextReply = this.getEmailTextReply(
				emailDetails,
				matchedText
			);
		} catch (err) {
			console.log('error ', err);
			mailData.emailTextReply =
				'Oops! This message is unsupported and cannot be displayed in Studio Ninja. Please view this message in your email provider';
			mailData.validMsg = false;
		}
		mailData.emailText = this.getReplyText(mailData);
		return mailData;
	}

	checkIfEmailAllowedWeb(contact, func, thisRef, argsArr) {
		this.emailFn = this.utilsService.wrapFunction(func, thisRef, argsArr);

		if (contact && contact.status === 'UNSUBSCRIBED') {
			this.utilsService.closeAllModalsAndOpenNew('#resubscribeClientModal');
		} else {
			this.callLastEmailFn();
		}
	}

	checkIsCCSubscribedWeb(contact, func, thisRef, argsArr) {
		this.emailFn = this.utilsService.wrapFunction(func, thisRef, argsArr);

		if (contact && contact.status === 'UNSUBSCRIBED') {
			$('#resubscribeClientModal').modal('show');
		} else {
			this.callLastEmailFn();
		}
	}

	callLastEmailFn(): void {
		this.emailFn();
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/email/email-service/email.service.ts